import * as fabric from 'office-ui-fabric-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useConfirmation } from '../../../../../components/dialogs';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import { useWindow } from '../../../../../components/windows/WindowContext';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import TicketTemplateCreateWindow from './TicketTemplateCreateWindow';
import TicketTemplateEditWindow from './TicketTemplateEditWindow';

export interface INewTicketTemplate {
    templateName: string | null;
    isLogoEnabled: boolean;
    isOrganizationNameEnabled: boolean;
    mainText: string | null;
    isMainTextEnabled: boolean;
    floorText: string | null;
    isFloorTextEnabled: boolean;
    cabinetText: string | null;
    isCabinetTextEnabled: boolean;
    timeText: string | null;
    isTimeTextEnabled: boolean;
    isBarcodeEnabled: boolean;
    isDateEnabled: boolean;
}

export const toggleButtonStyles = {
    container: {
        marginTop: '8px'
    }
};

export const RowStyles = {
    marginBottom: '20px'
};

export const explainTextPosition: React.CSSProperties = {
    paddingTop: '10px',
    marginTop: '10px'
};

export const explainTextFont: React.CSSProperties = {
    fontWeight: 'lighter'
};

export const validationSchema = Yup.object<INewTicketTemplate>().shape({
    templateName: Yup.string().required('Šis laukas yra privalomas'),
    mainText: Yup.string()
        .nullable()
        .when('isMainTextEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        }),
    floorText: Yup.string()
        .nullable()
        .when('isFloorTextEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        }),
    cabinetText: Yup.string()
        .nullable()
        .when('isCabinetTextEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        }),
    timeText: Yup.string()
        .nullable()
        .when('isTimeTextEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        })
});

const getTicketTemplates = async (organizationInformationId: number, skip: number, take: number) =>
    api.ticketTemplate.getTicketTemplates({
        organizationInformationId,
        skip,
        take
    });

const GeneralSettingsTicketsTemplates = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();
    const { confirmAsync } = useConfirmation();
    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const { openWindow } = useWindow();
    const [isLoading, setIsLoading] = useState(false);
    const [ticketTemplates, refetchTicketTemplates, , , isTicketTemplatesLoading] = useFetchedValue(
        getTicketTemplates,
        true,
        parseInt(organizationInformationId.toString()),
        gridPageProps.skip,
        gridPageProps.take
    );

    const openTicketTemplateEditModalWindow = async (ticketTemplateId: number) => {
        try {
            TicketTemplateEditWindow.defaultProps = {
                componentProps: {
                    ticketTemplateId: ticketTemplateId,
                    refetchTicketTemplates: refetchTicketTemplates
                }
            };
            openWindow({
                title: 'Bilietų šablono redagavimas',
                component: TicketTemplateEditWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openTicketTemplateCreateModalWindow = async () => {
        try {
            TicketTemplateCreateWindow.defaultProps = {
                componentProps: {
                    refetchTicketTemplates: refetchTicketTemplates
                }
            };
            openWindow({
                title: 'Bilietų šablono kūrimas',
                component: TicketTemplateCreateWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const deleteTicketTemplate = async (id: number) => {
        try {
            const { success } = await confirmAsync(
                'Šablono ištrynimas',
                'Ar tikrai norite ištrinti pasirinktą šabloną? Ištrynę šabloną, jo atkurti nebegalėsite'
            );

            if (!success) {
                return;
            }
            setIsLoading(true);
            await api.ticketTemplate.deleteTicketTemplate(id);
            toast.success('Šablonas sėkmingai ištrintas');
            refetchTicketTemplates();
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        <KendoLoading />;
    }

    return (
        <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row ms-slideRightIn40">
                <div style={{ padding: '10px', textAlign: 'center' }} className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLarge">
                        Šablonai
                    </fabric.Text>
                </div>
            </div>
            <ListItemCreateButton openCreateModal={openTicketTemplateCreateModalWindow} />

            <Grid className="mt-2" {...gridPageProps} data={ticketTemplates} isLoading={isTicketTemplatesLoading}>
                <GridColumn
                    width={100}
                    title="Veiksmai"
                    cell={(c) => (
                        <AdministrationActionCell
                            {...c}
                            fetchData={refetchTicketTemplates}
                            deleteItem={deleteTicketTemplate}
                            editItem={openTicketTemplateEditModalWindow}
                            locked={!c.dataItem.isEditable}
                        />
                    )}
                />
                <GridColumn width={400} title="Pavadinimas" field="name" />

                <GridColumn
                    width={150}
                    title="Logotipas"
                    field="isLogoEnabled"
                    cell={(c) => <td>{c.dataItem.isLogoEnabled ? 'Rodomas' : 'Nerodomas'}</td>}
                />
                <GridColumn
                    width={150}
                    title="Barkodas"
                    field="isBarcodeEnabled"
                    cell={(c) => <td>{c.dataItem.isBarcodeEnabled ? 'Rodomas' : 'Nerodomas'}</td>}
                />
                <GridColumn
                    width={150}
                    title="Data"
                    field="isDateEnabled"
                    cell={(c) => <td>{c.dataItem.isDateEnabled ? 'Rodoma' : 'Nerodoma'}</td>}
                />
                <GridColumn
                    title="Tipas"
                    field="isEditable"
                    cell={(c) => <td>{c.dataItem.isEditable ? 'Sukurtas šablonas' : 'Bendrinis šablonas'}</td>}
                />
            </Grid>
        </div>
    );
};

export default GeneralSettingsTicketsTemplates;
